import { applyBoardSeed, type BoardVersionSeed } from './exerciseBoardModel'
import { compareStory, storyDeltaMs } from './storyTime'
import type {
  ExerciseRun,
  ScenarioAction,
  ScenarioObject,
  ScenarioSession,
} from './types'

/** Object + clock kinds for tape review slice 1. */
export const TAPE_OBJECT_KINDS = new Set([
  'object.place',
  'object.move',
  'object.edit',
  'object.remove',
])

export const TAPE_CLOCK_KINDS = new Set([
  'run.start',
  'clock.run',
  'clock.pause',
  'clock.resume',
  'clock.seek',
  'clock.stop',
  'clock.setRate',
  'clock.previewPlay',
])

/** Actions in a run’s seq window (inclusive). Missing end → open through max seq. */
export function actionsInRunSlice(
  actions: readonly ScenarioAction[],
  seqStart: number | null | undefined,
  seqEnd: number | null | undefined,
): ScenarioAction[] {
  if (seqStart == null || !Number.isFinite(seqStart)) return []
  const lo = seqStart
  const hi = seqEnd != null && Number.isFinite(seqEnd) ? seqEnd : Number.POSITIVE_INFINITY
  return actions
    .filter((a) => typeof a.seq === 'number' && a.seq >= lo && a.seq <= hi)
    .sort((a, b) => a.seq - b.seq || a.actualTime.localeCompare(b.actualTime))
}

function objectFromPayload(payload: Record<string, unknown>): ScenarioObject | null {
  const raw = payload.object
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as ScenarioObject
  if (typeof obj.id !== 'string' || !obj.id.trim()) return null
  return obj
}

function objectIdFromPayload(payload: Record<string, unknown>): string | null {
  const id = payload.objectId
  if (typeof id === 'string' && id.trim()) return id.trim()
  const obj = objectFromPayload(payload)
  return obj?.id ?? null
}

/** Apply object.* tape rows onto a board seed (mutates via clone). */
export function applyTapeObjectActions(
  seed: BoardVersionSeed,
  actions: readonly ScenarioAction[],
): BoardVersionSeed {
  let objects = structuredClone(seed.objects ?? [])
  for (const action of actions) {
    if (!TAPE_OBJECT_KINDS.has(action.kind)) continue
    const payload = action.payload ?? {}
    if (action.kind === 'object.remove') {
      const id = objectIdFromPayload(payload)
      if (!id) continue
      objects = objects.filter((o) => o.id !== id)
      continue
    }
    const next = objectFromPayload(payload)
    if (!next) continue
    const idx = objects.findIndex((o) => o.id === next.id)
    if (idx >= 0) objects[idx] = structuredClone(next)
    else objects.push(structuredClone(next))
  }
  return {
    ...structuredClone(seed),
    objects,
  }
}

/**
 * Board at a story playhead: start seed + object actions whose storyTime has fired
 * (or that lack storyTime — treat as already applied).
 */
export function boardAtTapePlayhead(
  seed: BoardVersionSeed,
  actions: readonly ScenarioAction[],
  playheadIso: string,
): BoardVersionSeed {
  const due = actions.filter((a) => {
    if (!TAPE_OBJECT_KINDS.has(a.kind)) return false
    if (!a.storyTime) return true
    return compareStory(a.storyTime, playheadIso) <= 0
  })
  return applyTapeObjectActions(seed, due)
}

/** Read-only session view for tape review (does not mutate the working board). */
export function sessionForTapeReview(
  liveSession: ScenarioSession,
  seed: BoardVersionSeed,
  actions: readonly ScenarioAction[],
  playheadIso: string,
): ScenarioSession {
  const board = boardAtTapePlayhead(seed, actions, playheadIso)
  return applyBoardSeed(liveSession, board)
}

export function runHasTapeSeed(run: { seed?: BoardVersionSeed | null } | null | undefined): boolean {
  return Boolean(run?.seed && Array.isArray(run.seed.objects))
}

function storyMsFromAction(
  action: ScenarioAction,
  storyStartIso: string,
): number | null {
  const direct = action.storyTime
  if (typeof direct === 'string' && direct.trim()) {
    const ms = storyDeltaMs(storyStartIso, direct)
    if (Number.isFinite(ms)) return Math.floor(ms)
  }
  const payload = action.payload ?? {}
  const nested = payload.storyTime
  if (typeof nested === 'string' && nested.trim()) {
    const ms = storyDeltaMs(storyStartIso, nested)
    if (Number.isFinite(ms)) return Math.floor(ms)
  }
  return null
}

/**
 * Story-ms boundary for tape review scrub/play. Prefer the stamped stop playhead;
 * legacy runs fall back to the last stop/pause storyTime in the ActionsJson slice,
 * then any object/clock storyTime (not an open-ended max that can swallow blackout).
 */
export function resolveTapeAvailableUntilMs(
  run: Pick<ExerciseRun, 'stoppedStoryElapsedMs'> | null | undefined,
  storyStartIso: string,
  actions: readonly ScenarioAction[] = [],
): number | null {
  if (
    run &&
    typeof run.stoppedStoryElapsedMs === 'number' &&
    Number.isFinite(run.stoppedStoryElapsedMs)
  ) {
    return Math.max(0, Math.floor(run.stoppedStoryElapsedMs))
  }
  if (!storyStartIso) return null
  // Ignore post-stop Preview/seek clock rows (open seqEnd); use pause/stop + object moves.
  // A lone Pause/Seek at story start (Start Run settle) must not win over later object
  // moves — that stamped stoppedStoryElapsedMs=0 and clamped the Reviewing bar to 0.
  let stopBoundary = -1
  let objectBoundary = -1
  for (const action of actions) {
    const ms = storyMsFromAction(action, storyStartIso)
    if (ms == null) continue
    if (
      action.kind === 'clock.pause' ||
      action.kind === 'clock.stop' ||
      action.kind === 'lifecycle.end'
    ) {
      if (ms > stopBoundary) stopBoundary = ms
      continue
    }
    if (TAPE_OBJECT_KINDS.has(action.kind) && ms > objectBoundary) {
      objectBoundary = ms
    }
  }
  if (objectBoundary >= 0 && stopBoundary >= 0) {
    return Math.max(stopBoundary, objectBoundary)
  }
  if (objectBoundary >= 0) return objectBoundary
  // Pause-only: ignore near-zero (Start Run / countdown Seek) — not a real stop stamp.
  if (stopBoundary > 250) return stopBoundary
  return null
}
